/**
 * Formatte les réponses de l'API selon le format standard utilisé par l'API Road Alert
 * 
 * Format de réponse attendu :
 * {
 *   status: { code: string, message: string },
 *   hasError: boolean,
 *   count?: number,
 *   items?: T[],
 *   data?: T | T[],
 *   content?: T[]
 * }
 */

export interface IGlobalResponse<T> {
  success: boolean;
  message: string;
  data: T;
  other?: any;
  errors?: Record<string, string[]>;
  status: number;
}

/**
 * Formate une réponse API en un format standardisé et utilisable
 * 
 * @param response - La réponse brute de l'API
 * @param options - Options de formatage
 * @returns Un objet formaté avec les données, le count, et les métadonnées
 * 
 * @example
 * const response = await getCommunesByCriteria({ data: {} });
 * const formatted = formatApiResponse(response);
 * // formatted.data contient le tableau des communes
 * // formatted.count contient le nombre total
 */
export const formatApiResponse = (apiResponse: any): IGlobalResponse<any> => {
  // Si c'est une ErrorResponse du backend (format Spring Boot)
  if (apiResponse && typeof apiResponse === 'object' && 'message' in apiResponse && 'status' in apiResponse && 'timestamp' in apiResponse) {
      return {
          success: false,
          message: apiResponse.message || 'Une erreur est survenue',
          data: null,
          other: {
              count: 0,
              errorDetails: apiResponse.details || {},
          },
          errors: apiResponse.details || {},
          status: apiResponse.status || 500
      };
  }

  // Support réponse Axios (body dans .data) ou body direct
  const body = apiResponse?.data !== undefined ? apiResponse.data : apiResponse;
  // Format ApiResponse standard du backend
  const { hasError, status, item, items, count, mapList, data, totalPages, currentPage, pageSize, listmap } = body || {};
  const codeNumber = parseInt(status?.code || '0', 10) || 0;

  return {
    success: !hasError,
    message: status?.message || '',
    data: item !== undefined && item !== null ? item : (items || mapList || data || listmap || null),
    other: {
      count: count ?? 0,
      totalPages: totalPages ?? undefined,
      currentPage: currentPage ?? undefined,
      pageSize: pageSize ?? undefined,
    },
    errors: undefined,
    status: isNaN(codeNumber) ? 0 : codeNumber
  };
};